const rawPublicUrl = (process.env.PUBLIC_URL ?? "").trim();

const absoluteUrlPattern = /^[a-z][a-z\d+\-.]*:\/\//i;

function stripTrailingSlash(value: string): string {
  return value.length > 1 ? value.replace(/\/+$/, "") : value;
}

function ensureLeadingSlash(value: string): string {
  return value.startsWith("/") ? value : `/${value}`;
}

function isWithinPublicPath(pathname: string, base: string): boolean {
  return base === "/" ? pathname.startsWith("/") : pathname === base || pathname.startsWith(`${base}/`);
}

function joinUrl(base: string, path = ""): string {
  const normalizedBase = stripTrailingSlash(base);
  const normalizedPath = path ? ensureLeadingSlash(path) : "";

  if (!normalizedBase || normalizedBase === "/") {
    return normalizedPath || "/";
  }

  return `${normalizedBase}${normalizedPath}`;
}

export function getPublicPath(): string {
  const publicUrl = stripTrailingSlash(rawPublicUrl);

  if (!publicUrl) {
    return "";
  }

  try {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "http://localhost";
    return stripTrailingSlash(new URL(publicUrl, origin).pathname);
  } catch {
    return stripTrailingSlash(ensureLeadingSlash(publicUrl));
  }
}

export function toPublicUrl(path = ""): string {
  if (absoluteUrlPattern.test(path)) {
    return path;
  }

  return joinUrl(rawPublicUrl, path);
}

export function toAbsolutePublicUrl(path = ""): string {
  if (absoluteUrlPattern.test(path)) {
    return path;
  }

  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  return new URL(toPublicUrl(path), origin).toString();
}

export function getCurrentPublicUrl(): string {
  if (typeof window === "undefined") {
    return toAbsolutePublicUrl("/");
  }

  const currentUrl = new URL(window.location.href);
  const publicPath = getPublicPath();

  if (publicPath && !isWithinPublicPath(currentUrl.pathname, publicPath)) {
    currentUrl.pathname = joinUrl(publicPath, currentUrl.pathname);
  }

  return currentUrl.toString();
}

export function toAppPathname(path: string): string {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";

  let pathname = path;

  try {
    pathname = new URL(path, origin).pathname;
  } catch {
    pathname = ensureLeadingSlash(pathname);
  }

  const publicPath = getPublicPath();

  if (publicPath && isWithinPublicPath(pathname, publicPath)) {
    const appPath = pathname.slice(publicPath.length);
    return appPath ? ensureLeadingSlash(appPath) : "/";
  }

  return ensureLeadingSlash(pathname);
}
